import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa';
import {
  HiOutlineUser,
  HiOutlineMail,
  HiOutlineLockClosed,
  HiX,
  HiOutlinePlus
} from 'react-icons/hi';
import axiosInstance from '../../../services/AxiosInstance';

const CreateAdminModal = ({ onAdminCreated, orgId, show, buttonLabel = "Add Administrator" }) => {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('ADMIN');
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowModal(false);
      }
    };

    if (showModal) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  const handleValidationError = (errorMessage) => {
    if (errorMessage.includes('name')) {
      return 'Invalid name provided.';
    } else if (errorMessage.includes('email')) {
      return 'Invalid email provided.';
    } else if (errorMessage.includes('password')) {
      return 'Invalid password provided.';
    }
    return 'Validation error occurred.';
  };

  const handleClose = () => {
    if (!loading) {
      setShowModal(false);
    }
  };

  const handleShow = () => {
    setShowModal(true);
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    if (password.length < minLength) {
      return "Password must be at least 8 characters long.";
    }
    if (!hasUppercase) {
      return "Password must contain at least one uppercase letter.";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character.";
    }
    return "";
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(validatePassword(value));
  };

  const createAdmin = async () => {
    let hasError = false;
    if (!name.trim()) {
      toast.error('Name is required');
      hasError = true;
    }
    if (!email.trim()) {
      toast.error('Email is required');
      hasError = true;
    }
    if (!password) {
      toast.error('Password is required');
      hasError = true;
    }
    if (passwordError) {
      hasError = true;
    }

    if (hasError) {
      return;
    }

    if (!orgId) {
      toast.error('Organisation ID is missing');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`/v1/organization/${orgId}/user`, {
        name,
        email,
        password,
        role
      });

      setName('');
      setEmail('');
      setPassword('');
      handleClose();
      toast.success('Admin created successfully');
      if (onAdminCreated) {
        onAdminCreated();
      }
      navigate(`/super-admin/edit-organisation/${orgId}`);
    } catch (error) {
      const errorMessage = error.response?.data?.message;
      if (errorMessage?.includes('E11000 duplicate key error')) {
        if (errorMessage.includes('name_1 dup key')) {
          toast.error(`An Admin with the name "${name}" already exists.`);
        } else if (errorMessage.includes('email_1 dup key')) {
          toast.error(`An Admin with the email "${email}" already exists.`);
        } else {
          toast.error('This admin already exists.');
        }
      } else {
        const apiMessage = error.response?.data?.message;
        toast.error(apiMessage || handleValidationError(apiMessage) || "An error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* <button
        onClick={handleShow}
        className="px-4 py-2 text-sm font-medium text-white bg-[#166473] rounded-lg hover:bg-[#1a7a8c] focus:ring-4 focus:ring-[#166473]/20 focus:outline-none transition-colors mt-2"
      >
        Add Admin
      </button> */}

      <button
        type="button"
        className="w-full p-5 border-2 border-dashed border-gray-200 rounded-xl bg-transparent text-gray-500 flex items-center justify-center gap-2 hover:border-[#166473] hover:text-[#166473] transition-colors"
        onClick={handleShow}
      >
        <HiOutlinePlus size={20} />
        <span className="font-medium">{buttonLabel}</span>
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose}></div>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="relative w-full max-w-md bg-white rounded-xl shadow-lg">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-[#166473]">Create a New Admin</h4>
                    <p className="text-sm text-gray-500">Add an administrator to manage this organisation</p>
                  </div>
                  <button
                    className="p-2 text-gray-400 hover:text-gray-500 transition-colors"
                    onClick={handleClose}
                  >
                    <HiX size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name<span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="absolute left-3 top-9 text-gray-400">
                      <HiOutlineUser size={18} />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#166473]/20 focus:border-[#166473] outline-none transition-colors"
                      placeholder="Enter admin name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email<span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="absolute left-3 top-9 text-gray-400">
                      <HiOutlineMail size={18} />
                    </div>
                    <input
                      type="email"
                      className="w-full pl-10 pr-4 py-2.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#166473]/20 focus:border-[#166473] outline-none transition-colors"
                      placeholder="Enter admin email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password<span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                        <HiOutlineLockClosed size={18} />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="w-full pl-10 pr-10 py-2.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#166473]/20 focus:border-[#166473] outline-none transition-colors"
                        placeholder="Enter password"
                        value={password}
                        onChange={handlePasswordChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </button>
                    </div>
                    {passwordError && (
                      <p className="mt-1 text-xs text-red-500">{passwordError}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 focus:outline-none transition-colors"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-6 py-2.5 text-sm font-medium text-white bg-[#166473] rounded-lg hover:bg-[#1a7a8c] focus:ring-4 focus:ring-[#166473]/20 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={createAdmin}
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Creating...</span>
                      </div>
                    ) : (
                      'Create Admin'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateAdminModal;
